import axios, { type AxiosResponse } from "axios";
import { type SortingDirection } from "enums";
import { type TagsToContent } from "interfaces";
import { type BatchResponse } from "interfaces/batchResponse";
import { type DistributedOperationResult } from "types";
import { type Filters, serializeArrayAndFilterNullable } from "utils/queryUtils";
import {
  type EmbeddedVideoLineChart,
  type EmbeddedVideoEngagement,
  type EmbeddedVideoGroup,
  type EmbeddedVideoUser,
  type EmbeddedVideoPeopleDetails,
} from "../types/models";
import {
  type EmbeddedVideo,
  type EmbeddedVideoPayload,
  type Publisher,
  type SasToken,
  type TagsOptions,
} from "./types";
import { type BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import { type ChartPeriod } from "hooks/useChartPeriodMeasure";

const embeddedVideosApiUrl = "/api/assets/embedded-videos";

export const embeddedVideosDataService = {
  generateSas(): Promise<AxiosResponse<SasToken>> {
    return axios.post(`${embeddedVideosApiUrl}/generateSas`);
  },
  getEmbeddedVideo(id: number): Promise<AxiosResponse<EmbeddedVideo>> {
    return axios.get(`${embeddedVideosApiUrl}/${id}`);
  },
  updateEmbeddedVideo(id: number, payload: EmbeddedVideoPayload): Promise<AxiosResponse> {
    return axios.put(`${embeddedVideosApiUrl}/${id}`, payload);
  },
  deleteEmbeddedVideos(ids: number[]): Promise<AxiosResponse<DistributedOperationResult>> {
    return axios.delete(embeddedVideosApiUrl, { data: { ids } });
  },
  duplicateEmbeddedVideos(ids: number[]): Promise<AxiosResponse<DistributedOperationResult>> {
    return axios.post(`${embeddedVideosApiUrl}/duplicate`, { ids });
  },
  changeEmbeddedVideosVisibility(
    ids: number[],
    visibility: boolean,
  ): Promise<AxiosResponse<DistributedOperationResult>> {
    return axios.post(`${embeddedVideosApiUrl}/visibility`, {
      ids,
      visibility,
    });
  },
  getTagsFilterOptions(showPurchased: boolean): Promise<AxiosResponse<TagsOptions>> {
    return axios.get(`${embeddedVideosApiUrl}/tags`, { params: { showPurchased } });
  },
  getPublishersFilterOptions(): Promise<AxiosResponse<Publisher[]>> {
    return axios.get(`${embeddedVideosApiUrl}/publishers`);
  },
  addTagsToEmbeddedVideos(data: TagsToContent): Promise<AxiosResponse<BatchResponse>> {
    return axios.post(`${embeddedVideosApiUrl}/tags`, data);
  },
  getPublishedEmbeddedVideosByIds(ids: string[], showPurchased: boolean) {
    const config = {
      params: {
        showPurchased,
        ids,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    return axios.get(`${embeddedVideosApiUrl}/published`, config);
  },
  saveEmbeddedVideoTags(id: number, payload: TagsToContent): Promise<AxiosResponse> {
    return axios.put(`${embeddedVideosApiUrl}/${id}/tags`, payload);
  },
  updateThumbnail(id: number, thumbnailPath: string): Promise<AxiosResponse> {
    return axios.put(`${embeddedVideosApiUrl}/${id}/thumbnail`, {
      thumbnailPath,
    });
  },
  getEmbeddedVideoUsers(
    embeddedVideoId: number,
    skip: number,
    top: number,
    sortBy: string,
    sortOrder: SortingDirection,
    filters: Filters,
    term?: string,
  ): Promise<AxiosResponse<EmbeddedVideoUser[]>> {
    return axios.get(`/api/v4/content/embeddedVideo/${embeddedVideoId}/users`, {
      params: {
        skip,
        top,
        sortBy,
        sortOrder,
        ...filters,
        term,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getEmbeddedVideoGroups(
    embeddedVideoId: number,
    skip: number,
    top: number,
    filters: Filters,
    term?: string,
  ): Promise<AxiosResponse<EmbeddedVideoGroup[]>> {
    return axios.get(`/api/v4/content/embeddedVideo/${embeddedVideoId}/groups`, {
      params: {
        skip,
        top,
        ...filters,
        term,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getEmbeddedVideoLineChart(
    params: BasePerformanceRequestFilterParams,
    embeddedVideoId: number,
    period: ChartPeriod,
    signal: AbortSignal,
  ): Promise<AxiosResponse<EmbeddedVideoLineChart[]>> {
    return axios.get(`/api/reports/v1/embeddedvideos/linechart`, {
      params: {
        embeddedVideoId,
        period,
        ...params,
      },
      signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getEmbeddedVideoEngagement(
    params: BasePerformanceRequestFilterParams,
    embeddedVideoId: number,
    signal: AbortSignal,
  ): Promise<AxiosResponse<EmbeddedVideoEngagement>> {
    return axios.get(`/api/reports/v1/embeddedvideos/engagement`, {
      params: {
        embeddedVideoId,
        ...params,
      },
      signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getEmbeddedVideoPeopleDetails(
    params: BasePerformanceRequestFilterParams,
    embeddedVideoId: number,
    signal: AbortSignal,
  ): Promise<AxiosResponse<EmbeddedVideoPeopleDetails[]>> {
    return axios.get("/api/reports/v1/embeddedvideos/people-details", {
      params: {
        embeddedVideoId,
        ...params,
      },
      signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getEmbeddedVideoExport(
    params: BasePerformanceRequestFilterParams,
    embeddedVideoId: number,
  ): Promise<AxiosResponse<EmbeddedVideoPeopleDetails[]>> {
    return axios.get("/api/dataexport/v1/embeddedvideos/user-progress", {
      responseType: "blob",
      params: {
        embeddedVideoId,
        ...params,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
};
